import React, { useEffect, useState } from "react";
import { Pause, Play, Zap, Cpu, RefreshCw, AlertTriangle } from "lucide-react";
import LabShell from "./LabShell";
import { Panel } from "./labUi";
import {
    fetchPipelineState,
    fetchEndpoint,
    fetchHealth,
    pausePipeline,
    resumePipeline,
    triggerRun,
    isBackendUnavailable,
} from "../../services/LabService";

/**
 * "Settings" v1 was a fiction — the documented backend has no per-user
 * settings endpoint. This is now a Pipeline Controls page that wraps the
 * three real surfaces the backend exposes:
 *   - POST /control/pause | /control/resume + GET /control/state
 *   - GET /endpoint  (SageMaker describe_endpoint passthrough)
 *   - POST /runs:trigger (manually claim the next inbox item)
 */

const Field = ({ label, value, mono = true }) => (
    <div className="flex items-center justify-between py-1 text-xs">
        <span className="text-slate-500">{label}</span>
        <span className={`${mono ? "font-mono" : ""} text-slate-700 dark:text-slate-200`}>{value ?? "—"}</span>
    </div>
);

const LabControls = () => {
    const [pipeline, setPipeline] = useState(null);
    const [endpoint, setEndpoint] = useState(null);
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const [h, p, e] = await Promise.allSettled([
                fetchHealth(),
                fetchPipelineState(),
                fetchEndpoint(),
            ]);
            if (h.status === "fulfilled") setHealth(h.value);
            if (p.status === "fulfilled") setPipeline(p.value);
            if (e.status === "fulfilled") setEndpoint(e.value);
            // If everything failed with backend-unavailable, flag preview mode.
            const allUnavailable = [h, p, e].every(
                (r) => r.status === "rejected" && isBackendUnavailable(r.reason)
            );
            setPreview(allUnavailable);
            const firstReal = [h, p, e].find(
                (r) => r.status === "rejected" && !isBackendUnavailable(r.reason)
            );
            setError(firstReal?.reason || null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, []);

    const togglePause = async () => {
        if (!pipeline) return;
        setBusy(true);
        try {
            const next = pipeline.paused ? await resumePipeline() : await pausePipeline();
            setPipeline(next);
        } catch (err) {
            alert(err.message);
        } finally {
            setBusy(false);
        }
    };

    const trigger = async () => {
        if (!window.confirm("Trigger one pipeline run? It will claim the next pending inbox item.")) return;
        setBusy(true);
        try {
            await triggerRun();
            reload();
        } catch (err) {
            alert(err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <LabShell
            activeKey="settings"
            headerAction={
                <button
                    onClick={reload}
                    disabled={loading}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
                >
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
            }
        >
            {preview ? (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                        Backend not deployed — the controls below are wired against the
                        documented contract but will no-op until <code className="font-mono">/api/lab/*</code> ships.
                    </div>
                </div>
            ) : null}

            {error ? (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error.message}</div>
            ) : null}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Panel
                    title={
                        <span className="flex items-center gap-2">
                            {pipeline?.paused ? <Pause className="w-3.5 h-3.5 text-amber-500" /> : <Play className="w-3.5 h-3.5 text-emerald-500" />}
                            Pipeline
                        </span>
                    }
                    action={
                        <button
                            onClick={togglePause}
                            disabled={busy || preview}
                            className={`text-xs px-2.5 py-1 rounded-md text-white disabled:opacity-50 ${
                                pipeline?.paused
                                    ? "bg-emerald-600 hover:bg-emerald-700"
                                    : "bg-amber-500 hover:bg-amber-600"
                            }`}
                        >
                            {pipeline?.paused ? "Resume" : "Pause"}
                        </button>
                    }
                >
                    <Field
                        label="State"
                        value={pipeline?.paused ? "Paused" : "Live"}
                        mono={false}
                    />
                    <Field label="Since" value={pipeline?.since || "—"} />
                    <Field label="State machine" value={health?.pipeline?.state_machine || "alpamayo-experiment-pipeline"} />
                    <Field label="Currently running" value={health?.pipeline?.currently_running_executions ?? "—"} />
                    <Field label="Inbox pending" value={health?.pipeline?.inbox_pending_count ?? "—"} />
                    <Field label="Done last 24h" value={health?.pipeline?.inbox_done_count_24h ?? "—"} />

                    <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-800">
                        <button
                            onClick={trigger}
                            disabled={busy || preview}
                            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
                        >
                            <Zap className="w-3.5 h-3.5" /> Trigger one run
                        </button>
                        <p className="mt-1.5 text-[11px] text-slate-500">
                            Manually fires one Step Function execution that claims the next pending inbox item.
                        </p>
                    </div>
                </Panel>

                <Panel
                    title={
                        <span className="flex items-center gap-2">
                            <Cpu className="w-3.5 h-3.5 text-blue-500" /> SageMaker endpoint
                        </span>
                    }
                >
                    <Field label="Name" value={endpoint?.name} />
                    <Field
                        label="Status"
                        value={
                            <span className={endpoint?.status === "InService" ? "text-emerald-600" : "text-slate-500"}>
                                {endpoint?.status || "—"}
                            </span>
                        }
                        mono={false}
                    />
                    <Field label="Instance type" value={endpoint?.instance_type} />
                    <Field
                        label="Instances"
                        value={
                            endpoint
                                ? `${endpoint.current_instances ?? "?"} / desired ${endpoint.desired_instances ?? "?"} (max ${endpoint.max_instances ?? "?"})`
                                : "—"
                        }
                    />
                    <Field label="Config" value={endpoint?.config_name} />
                    <Field label="Model" value={endpoint?.model_name} />
                    <Field label="Created" value={endpoint?.created_at} />
                    <Field label="Last modified" value={endpoint?.last_modified} />
                </Panel>
            </div>
        </LabShell>
    );
};

export default LabControls;
